import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, styled, Button, Paper, Dialog } from '@mui/material';
import DialogTransition from '../DialogTransition';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import CollapsibleDataGrid from '../../tables/CollapsibleDataGrid';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { TripLegInvoiceItem } from '../../../data/models/trip-leg.model';
import AcceptTermsDialog from './AcceptTermsDialog';
import { FullScreenDialogTitle } from '../../dialogs/FullScreenDialogTitle';
import { FullScreenDialogContent } from '../../dialogs/FullScreenDialogContent';
import { attestationUtils } from '../../../services/attestationUtils';
import { financialUtils } from '../../../services/financialUtils';
import type { GroupedTripsByDate } from '../../../data/models/invoice.model';
import { selectTimeZones } from '../../../data/reference.slice';
import DateUtils from '../../../services/DateUtils';
import { DateTime } from 'luxon';
import { TripLegStatusBC } from '../../../data/models/trip-leg-status.model';
import { checkTripLegReadyToInvoice } from '../../../utils/timestampValidation';
import { TripLegSearchResponseItem } from '../../../data/models/trip-leg-search.model';

const PREFIX = 'GenerateInvoice';

const classes = {
  container: `${PREFIX}-container`,
  gridContainer: `${PREFIX}-gridContainer`,
  bottomButtonGroup: `${PREFIX}-bottomButtonGroup`,
  bottomNextButton: `${PREFIX}-bottomNextButton`,
  bottomCancelButton: `${PREFIX}-bottomCancelButton`,
  totalCalculation: `${PREFIX}-totalCalculation`,
  calculationText: `${PREFIX}-calculationText`,
  bottomContainer: `${PREFIX}-bottomContainer`,
  collapseText: `${PREFIX}-collapseText`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.container}`]: {
    width: '100%',
    maxWidth: 1152,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  [`& .${classes.gridContainer}`]: {
    padding: '40px 40px 48px 40px',
    color: 'rgba(1, 16, 18, 0.90)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  [`& .${classes.collapseText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
  },
  [`& .${classes.bottomContainer}`]: {
    paddingBottom: '48px',
  },
  [`& .${classes.totalCalculation}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '65px',
  },
  [`& .${classes.calculationText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '21px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
  },
  [`& .${classes.bottomButtonGroup}`]: {
    marginTop: '32px',
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.bottomNextButton}`]: {
    display: 'flex',
    width: '144px',
    height: '48px',
    minWidth: '144px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  [`& .${classes.bottomCancelButton}`]: {
    display: 'flex',
    width: '144px',
    height: '48px',
    minWidth: '144px',
    marginLeft: '16px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));
interface GenerateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onInvoiceGenerated?: () => void;
  tripLegs: TripLegSearchResponseItem[];
}

export default function GenerateInvoiceDialog({
  open,
  onClose,
  onInvoiceGenerated,
  tripLegs,
}: GenerateInvoiceDialogProps) {
  const { t } = useTranslation('common');
  const [acceptTermsDialogOpen, setAcceptTermsDialogOpen] = useState(false);

  const timezones = useAppSelector(selectTimeZones);

  const getFinalizedStatus = (tripLegStatusBC: string) => {
    switch (tripLegStatusBC) {
      case TripLegStatusBC.NoLoad:
        return TripLegStatusBC.NoLoad;
      case TripLegStatusBC.CanceledLate:
        return TripLegStatusBC.CanceledLate;
      case TripLegStatusBC.Completed:
        return TripLegStatusBC.Completed;
      default:
        return '';
    }
  };

  const invoiceTripLegRows: TripLegInvoiceItem[] = useMemo(() => {
    return tripLegs
      .map((tripLeg) => {
        const timezone = DateUtils.getTimeZoneIana(
          timezones,
          tripLeg?.pickUpAddressTimeZoneID ?? 0,
        );

        const baseAmount = financialUtils.parsePrice(
          (tripLeg?.payableAmount as string) ?? '0',
        );
        const addOnTotal = financialUtils.parsePrice(
          (tripLeg.addOnItemsPayableTotal as string) ?? '0',
        );

        const totalPrice = financialUtils.formatPrice(baseAmount + addOnTotal);

        return {
          ...tripLeg,
          id: tripLeg?.tripLegID ?? 0,
          tripLegReferenceID: tripLeg?.tripLegReferenceID ?? '',
          tripLegID: tripLeg?.tripLegID ?? 0,
          pickupTime: tripLeg?.isWillCall
            ? attestationUtils.formatDate(
                tripLeg?.appointmentDate ?? '',
                timezone,
                true,
              )
            : attestationUtils.formatTime(
                tripLeg?.pickUpDueTimeUTC ?? '',
                timezone,
              ),
          isWillCall: tripLeg?.isWillCall,
          tripID: tripLeg?.tripLegReferenceID,
          pickupAddress: [
            tripLeg?.pickUpAddressLine1,
            tripLeg?.pickUpAddressLine2,
          ]
            .filter(Boolean)
            .join(', '),
          pickupCity: [
            tripLeg?.pickUpCity,
            tripLeg?.pickUpStateCode,
            tripLeg?.pickUpZipCode,
          ]
            .filter((part) => !!part)
            .join(', '),
          dropoffAddress: [
            tripLeg?.dropOffAddressLine1,
            tripLeg?.dropOffAddressLine2,
          ]
            .filter(Boolean)
            .join(', '),
          dropoffCity: [
            tripLeg?.dropOffCity,
            tripLeg?.dropOffStateCode,
            tripLeg?.dropOffZipCode,
          ]
            .filter((part) => !!part)
            .join(', '),
          memberName: attestationUtils.getPassengerName(tripLeg),
          clientPassengerID: tripLeg?.clientPassengerID,
          driverName: attestationUtils.getDriverName(tripLeg),
          onSite: attestationUtils.formatTime(
            tripLeg?.tripLegStatusLogs?.find(
              ({ tripLegStatusBC }) => tripLegStatusBC === 'ONSITE',
            )?.createdOnUTC ?? '',
            timezone,
          ),
          onBoard: attestationUtils.formatTime(
            tripLeg?.tripLegStatusLogs?.find(
              ({ tripLegStatusBC }) => tripLegStatusBC === 'ONBOARD',
            )?.createdOnUTC ?? '',
            timezone,
          ),
          completed: attestationUtils.formatTime(
            tripLeg?.tripLegStatusLogs?.find(
              ({ tripLegStatusBC }) => tripLegStatusBC === 'COMPLETED',
            )?.createdOnUTC ?? '',
            timezone,
          ),
          noLoad: attestationUtils.formatTime(
            tripLeg?.tripLegStatusLogs?.find(
              ({ tripLegStatusBC }) => tripLegStatusBC === 'NOLOAD',
            )?.createdOnUTC ?? '',
            timezone,
          ),
          canceledLate: attestationUtils.formatTime(
            tripLeg?.tripLegStatusLogs?.find(
              ({ tripLegStatusBC }) => tripLegStatusBC === 'CANCELEDLATE',
            )?.createdOnUTC ?? '',
            timezone,
          ),
          distance: tripLeg?.invoiceDistanceMiles,
          price: totalPrice,
          hasRequiredTimestamps: true,
          isException: tripLeg?.isException,
          isReadyToInvoice: checkTripLegReadyToInvoice(tripLeg),
          tripLegState: tripLeg?.tripLegStatusBC,
          tripLegStatus: getFinalizedStatus(tripLeg?.tripLegStatusBC),
        };
      })
      .sort((a, b) => {
        return a.appointmentDate.localeCompare(b.appointmentDate);
      });
  }, [tripLegs, timezones]);

  const invoicableTripLegsByDate: GroupedTripsByDate[] = useMemo(() => {
    const groups: GroupedTripsByDate[] = [];

    for (const tripLeg of invoiceTripLegRows) {
      const date = DateUtils.formatDate(
        DateTime.fromISO(tripLeg.appointmentDate),
      );

      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.date === date) {
        lastGroup.rows.push(tripLeg);
      } else {
        groups.push({ date, rows: [tripLeg] });
      }
    }

    return groups;
  }, [invoiceTripLegRows]);

  const totalPriceFormatted = useMemo(() => {
    const total = tripLegs.reduce((sum, tripLeg) => {
      const baseAmount = financialUtils.parsePrice(
        (tripLeg?.payableAmount as string) ?? '0',
      );
      const addOnTotal = financialUtils.parsePrice(
        (tripLeg.addOnItemsPayableTotal as string) ?? '0',
      );
      return financialUtils.roundTo(sum + baseAmount + addOnTotal, 2);
    }, 0);

    return financialUtils.formatPrice(total);
  }, [tripLegs]);

  return (
    <>
      <StyledDialog
        fullScreen={true}
        open={open}
        onClose={onClose}
        TransitionComponent={DialogTransition}>
        <FullScreenDialogTitle
          title={t('generateInvoice.title')}
          close={onClose}
        />
        <FullScreenDialogContent>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box className={classes.container}>
              <Paper elevation={3}>
                <Box className={classes.gridContainer}>
                  {invoicableTripLegsByDate.map(({ date, rows }) => {
                    const dateTotalPrice = rows.reduce((sum, trip) => {
                      const numericPrice = Number(
                        (trip.price ?? '0').toString().replace(/[$,]/g, ''),
                      );
                      return financialUtils.roundTo(sum + numericPrice, 2);
                    }, 0);

                    const dateTotalPriceFormatted =
                      financialUtils.formatPrice(dateTotalPrice);

                    return (
                      <CollapsibleDataGrid
                        key={date}
                        title={`${date}: ${
                          rows.length > 1
                            ? t('generateInvoice.calculatedTotalPlural', {
                                numTrips: rows.length,
                                total: dateTotalPriceFormatted,
                              })
                            : t('generateInvoice.calculatedTotalSingular', {
                                numTrips: rows.length,
                                total: dateTotalPriceFormatted,
                              })
                        }`}
                        rows={rows}
                        timezones={timezones}
                        hideMoveToNeedsAttention={true}
                      />
                    );
                  })}
                  <Box className={classes.bottomContainer}>
                    <Box className={classes.totalCalculation}>
                      <Typography className={classes.calculationText}>
                        {invoiceTripLegRows.length > 1
                          ? t(
                              'generateInvoice.calculatedTotalInvoiceTripsPlural',
                              {
                                numTrips: invoiceTripLegRows.length,
                                total: totalPriceFormatted,
                              },
                            )
                          : t(
                              'generateInvoice.calculatedTotalInvoiceTripsSingular',
                              {
                                numTrips: invoiceTripLegRows.length,
                                total: totalPriceFormatted,
                              },
                            )}
                      </Typography>
                    </Box>
                    <Box className={classes.bottomButtonGroup}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setAcceptTermsDialogOpen(true)}
                        className={classes.bottomNextButton}>
                        {t('generateInvoice.next')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={onClose}
                        className={classes.bottomCancelButton}>
                        {t('generateInvoice.cancel')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </LocalizationProvider>
        </FullScreenDialogContent>
      </StyledDialog>
      <AcceptTermsDialog
        tripLegs={invoiceTripLegRows}
        open={acceptTermsDialogOpen}
        onClose={() => setAcceptTermsDialogOpen(false)}
        onGenerateInvoice={() => {
          setAcceptTermsDialogOpen(false);
          onClose();
          onInvoiceGenerated?.();
        }}
      />
    </>
  );
}
